/**
 * WEB-447 §4.3(b) — trigger-bus for downsell popup visibility on the public
 * paywall render. Two triggers are supported today:
 *  - `exit_intent`: the visitor's cursor leaves over the TOP of the viewport
 *    (classic "about to close the tab" signal).
 *  - `payment_declined`: the visitor was redirected back from Stripe with
 *    `?checkout=cancel` in the URL (session created but not completed), or
 *    checkout-session creation itself failed client-side.
 *
 * Both triggers latch: once fired, they stay fired for the rest of the
 * session (a re-render must not hide a popup the visitor already earned).
 * `dismiss()` also latches: once the visitor closes the popup it must not
 * reappear, even if another configured trigger fires afterward.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { detectBrowser } from '@/player/sources/ua-detect';

export type DownsellTriggerName = 'payment_declined' | 'exit_intent';

/** Допуск сверху для exit_intent (px): ловит быстрые уходы через верхний край. */
const EXIT_INTENT_TOP_PX = 10;

/**
 * WEB-2128 — СКОЛЬКО НАДО ОТСУТСТВОВАТЬ, ЧТОБЫ ЭТО СЧИТАЛОСЬ УХОДОМ.
 *
 * Вариант (В) карточки: скрытие страницы само по себе поводом не считается,
 * повод — ВОЗВРАЩЕНИЕ после долгого отсутствия. Полминуты — граница из самой
 * карточки: «возврат через полминуты — это действительно уход, а через
 * секунду — переключение».
 */
export const EXIT_INTENT_AWAY_MS = 30_000;

/**
 * WEB-2128 — встроенный браузер соцсети (Instagram/Facebook/TikTok).
 *
 * Реестр подписей ОДИН на продукт — `UA_SIGNATURES.inApp` в
 * `player/sources/ua-detect.ts`; своей копии списка здесь нет и быть не должно
 * (карточка прямо запрещает расширять распознавание в этой работе). Чтение
 * `navigator.userAgent` обёрнуто: в среде без navigator (SSR, часть тестов)
 * обращение бросает, и тогда трактуем как обычный браузер — прежнее поведение.
 */
function isInAppBrowser(): boolean {
  try {
    return detectBrowser(window.navigator.userAgent).inApp;
  } catch {
    return false;
  }
}

export interface DownsellTriggerContextValue {
  hasFired: (name: DownsellTriggerName) => boolean;
  fire: (name: DownsellTriggerName) => void;
  dismissed: boolean;
  dismiss: () => void;
  /**
   * WEB-2030: предложение погашено целиком — сильнее ЛЮБОГО повода.
   * Взводится отказом кассы, который дешёвым вариантом не лечится (реестр
   * `paywall-runtime/checkoutDownsellSuppression.ts`): причина не в цене, и на
   * другой цене человек упрётся в тот же отказ — второй тупик подряд (WEB-933).
   */
  suppressed: boolean;
  suppress: () => void;
}

// Safe no-op default: consumers rendered outside a DownsellTriggerProvider
// (e.g. the editor's own preview panel, or existing tests that don't wire
// the provider) behave as if no trigger has ever fired and nothing is
// dismissed — i.e. the pre-existing "always visible inline" behavior is
// unaffected for callers that never asked for gating.
const defaultContextValue: DownsellTriggerContextValue = {
  hasFired: () => false,
  fire: () => {},
  dismissed: false,
  dismiss: () => {},
  suppressed: false,
  suppress: () => {},
};

const DownsellTriggerContext = createContext<DownsellTriggerContextValue>(defaultContextValue);

export function useDownsellTrigger(): DownsellTriggerContextValue {
  return useContext(DownsellTriggerContext);
}

interface DownsellTriggerProviderProps {
  children: React.ReactNode;
}

export function DownsellTriggerProvider({
  children,
}: DownsellTriggerProviderProps): React.ReactElement {
  const [fired, setFired] = useState<ReadonlySet<DownsellTriggerName>>(() => new Set());
  const [dismissed, setDismissed] = useState(false);
  // WEB-2030: защёлка того же рода, что `dismissed` — однажды погасив, обратно
  // не включаем: повод, пришедший ПОСЛЕ отказа (увод курсора, сворачивание
  // вкладки), не имеет права показать предложение, которое заведомо не пройдёт.
  const [suppressed, setSuppressed] = useState(false);
  /**
   * WEB-2128: момент, когда страница скрылась (`null` — страница видима либо
   * скрытие пришло из встроенного браузера и в счёт не идёт). Повод взводится
   * не скрытием, а возвращением — см. эффект ниже.
   */
  const hiddenAtRef = useRef<number | null>(null);

  const fire = useCallback((name: DownsellTriggerName) => {
    setFired((prev) => (prev.has(name) ? prev : new Set([...prev, name])));
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const suppress = useCallback(() => {
    setSuppressed(true);
  }, []);

  const hasFired = useCallback((name: DownsellTriggerName) => fired.has(name), [fired]);

  // payment_declined via the `?checkout=cancel` URL marker set on Stripe's
  // cancel_url — read once on mount (the marker does not change during the
  // component's lifetime).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('checkout') === 'cancel') {
        fire('payment_declined');
      }
    } catch {
      // Defensive: non-browser test environments without window.location.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // exit_intent (QA 2026-07-17, ратиф. PM — A+B: надёжнее на десктопе + мобильные):
  //  A) desktop: курсор уходит через ВЕРХ окна (relatedTarget===null = ушёл за
  //     пределы окна). Небольшой допуск EXIT_INTENT_TOP_PX ловит быстрые уходы,
  //     когда clientY успевает стать чуть больше 0 (раньше строго ≤0 → «задержка»).
  //  B) mobile/touch + смена вкладки: `mouseout` там не существует → exit_intent
  //     не срабатывал вовсе. `visibilitychange` — единственный доступный там
  //     сигнал.
  //
  // WEB-2128 — ЧТО ИЗМЕНИЛОСЬ В (B) И ПОЧЕМУ. Раньше показ взводило САМО
  // скрытие страницы (`hidden`). Во встроенном браузере соцсети — а это
  // основной трафик клиентов — «страница скрылась» наступает от входящего
  // звонка, сворачивания приложения и переключения на ленту, то есть
  // постоянно и без намерения уйти. Вернувшийся человек видел модальное окно
  // со скидкой, которого не вызывал, а защёлка `fire` делала это необратимым:
  // настоящий уход позже уже ничего не показывал. Теперь:
  //
  //  · ВСТРОЕННЫЙ браузер (реестр `UA_SIGNATURES.inApp`) — скрытие страницы не
  //    повод НИКОГДА. Ничего не защёлкивается, поэтому уход курсора (если он
  //    там вдруг есть) по-прежнему сработает — карточка требует снять
  //    необратимость ложного срабатывания, а не завести вторую защёлку;
  //  · ОБЫЧНЫЙ браузер — вариант (В) карточки: повод не «страница скрылась», а
  //    «человек ВЕРНУЛСЯ после отсутствия дольше EXIT_INTENT_AWAY_MS».
  //    Возврат через секунду — переключение (звонок, уведомление), через
  //    полминуты — настоящий уход и есть что предложить.
  //
  // Момент ухода помним в ref, а не в state: перерисовка провайдеру здесь не
  // нужна, а лишняя перерисовка всего публичного дерева на каждое сворачивание
  // вкладки — нет.
  useEffect(() => {
    const handleMouseOut = (event: MouseEvent): void => {
      if (event.relatedTarget === null && event.clientY <= EXIT_INTENT_TOP_PX) {
        fire('exit_intent');
      }
    };
    const handleVisibility = (): void => {
      if (isInAppBrowser()) {
        // Встроенный браузер: ни момента ухода не запоминаем, ни повода не
        // взводим — сворачивание приложения здесь не значит ничего.
        hiddenAtRef.current = null;
        return;
      }
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt !== null && Date.now() - hiddenAt >= EXIT_INTENT_AWAY_MS) {
        fire('exit_intent');
      }
    };
    document.addEventListener('mouseout', handleMouseOut);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fire]);

  const value: DownsellTriggerContextValue = {
    hasFired,
    fire,
    dismissed,
    dismiss,
    suppressed,
    suppress,
  };

  return (
    <DownsellTriggerContext.Provider value={value}>
      {children}
    </DownsellTriggerContext.Provider>
  );
}
